import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import Nav from "@/components/welp/Nav";
import PageBg from "@/components/welp/PageBg";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Ban, MessageSquare, RotateCw, FileText, ArrowRight, Camera, Trash2, User, BarChart3, Phone, Activity } from "lucide-react";
import { resizeImageToDataUrl } from "@/lib/resizeImage";

export function NotificationsPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.notifications().then(d => setItems(d.notifications || []));
  }, [user, loading, nav]);

  return (
    <PageBg>
      <Nav />
      <section className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-veil">
        <div className="text-xs uppercase tracking-[0.32em] mb-3">
          <span className="text-[#10B981]">Welp</span> <span className="text-[#5A5A5A]">·</span> <span className="text-[#FF1A82]">Dating With Integrity</span> <span className="text-[#5A5A5A]">·</span> Inbox
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">Notifications</h1>
        </div>
        <div className="mt-10 space-y-3">
          {items.length === 0 && <p className="text-[#3A3A3A]">Nothing yet. When someone reviews a profile you've claimed — or the Welp team reaches out — you'll see it here.</p>}
          {items.map((n, i) => (
            <div key={n.notification_id} className={`rounded-2xl border p-5 animate-fade-up flex items-start gap-4 backdrop-blur-lg ${n.read ? "border-black/10 bg-white/70 backdrop-blur-lg shadow-lg" : "border-[#10B981]/40 bg-white/70 backdrop-blur-lg shadow-lg glow-pink"}`} style={{ animationDelay: `${i*60}ms` }} data-testid={`notification-${n.notification_id}`}>
              <Bell className={`w-5 h-5 mt-1 ${n.read ? "text-[#5A5A5A]" : "text-[#10B981]"}`} />
              <div className="flex-1">
                {n.type === "admin_message" && (
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#10B981] mb-1" data-testid={`admin-msg-badge-${n.notification_id}`}>From the Welp Team{n.category && n.category !== "general" ? ` · ${n.category}` : ""}</div>
                )}
                {n.subject && <div className="text-[#1A1A1A] font-semibold">{n.subject}</div>}
                <div className="text-[#3A3A3A] whitespace-pre-line">{n.message}</div>
                <div className="text-xs text-[#5A5A5A] mt-1">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              {!n.read && (
                <button onClick={async () => { await api.markRead(n.notification_id); setItems(it => it.map(x => x.notification_id === n.notification_id ? { ...x, read: true } : x)); }} className="text-xs text-[#FF1A82] hover:underline" data-testid={`mark-read-${n.notification_id}`}>Mark read</button>
              )}
            </div>
          ))}
        </div>
      </section>
    </PageBg>
  );
}

export function AccountPage() {
  const { user, loading, refresh, setUser } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (loading) return;
    if (!user) nav("/login");
  }, [user, loading, nav]);
  useEffect(() => { refresh(); }, [refresh]);
  if (!user) return null;

  const onPick = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image."); return; }
    // Accept up to 4MB source. We resize client-side so the payload sent to the
    // backend is always small.
    if (f.size > 4 * 1024 * 1024) { toast.error("Image must be under 4MB."); return; }
    setUploading(true);
    try {
      const b64 = await resizeImageToDataUrl(f, { maxDim: 1024, quality: 0.85 });
      const res = await api.uploadMyAvatar(b64);
      setUser(res.user);
      toast.success("Picture updated!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't upload picture.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const onRemove = async () => {
    setUploading(true);
    try {
      const res = await api.removeMyAvatar();
      setUser(res.user);
      toast.success("Picture removed.");
    } catch (err) {
      toast.error("Couldn't remove picture.");
    } finally { setUploading(false); }
  };

  return (
    <PageBg>
      <Nav />
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-[0.32em] mb-3">
          <span className="text-[#10B981]">Welp</span> <span className="text-[#5A5A5A]">·</span> <span className="text-[#FF1A82]">Dating With Integrity</span> <span className="text-[#5A5A5A]">·</span> Your account
        </div>

        {/* Avatar block */}
        <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6 mt-6" data-testid="account-avatar-block">
          <div className="relative">
            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-[#FF1A82] bg-[#FF1A82]/10 flex items-center justify-center shadow-lg">
              {user.picture ? (
                <img src={user.picture} alt="Your profile" className="w-full h-full object-cover" data-testid="account-avatar-img" />
              ) : (
                <span className="font-heading text-5xl font-bold text-[#FF1A82]">{(user.display_name || user.email || "?").charAt(0).toUpperCase()}</span>
              )}
            </div>
            <button onClick={onPick} disabled={uploading}
              className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-[#10B981] text-white flex items-center justify-center shadow-lg hover:brightness-110 disabled:opacity-60"
              data-testid="account-avatar-upload" aria-label="Upload picture">
              <Camera className="w-5 h-5" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="account-avatar-file" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">Hi, <span className="italic text-[#FF1A82]">{user.display_name?.split(" ")[0] || "friend"}.</span></h1>
            <div className="mt-2 flex flex-wrap gap-3 justify-center sm:justify-start">
              <button onClick={onPick} disabled={uploading} className="text-sm font-semibold text-[#FF1A82] hover:underline" data-testid="account-avatar-change">
                {uploading ? "Uploading…" : user.picture ? "Change picture" : "Upload picture"}
              </button>
              {user.picture && (
                <button onClick={onRemove} disabled={uploading} className="text-sm font-semibold text-[#10B981] hover:underline inline-flex items-center gap-1" data-testid="account-avatar-remove">
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              )}
            </div>
            <p className="text-sm text-[#1A1A1A] mt-2 font-semibold" data-testid="account-avatar-filetype-note">JPG or PNG, up to 4MB.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-10">
          <div className="rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7">
            <div className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] mb-2">Email</div>
            <div className="text-[#1A1A1A]">{user.email}</div>
          </div>
          <div className="rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7">
            <div className="text-xs uppercase tracking-[0.22em] text-[#10B981] mb-2">Display name</div>
            <div className="text-[#1A1A1A]">{user.display_name}</div>
          </div>
          <div className="rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7">
            <div className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] mb-2 flex items-center gap-2"><Ban className="w-4 h-4" /> Status</div>
            <div className={`text-2xl ${user.banned ? "text-[#10B981]" : "text-[#FF1A82]"}`}>{user.banned ? "Account restricted" : "Good standing"}</div>
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-4">
          <Link to="/account/reviews" data-testid="link-my-reviews"
            className="rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-[#10B981]/40 glow-pink p-7 hover:-translate-y-1 transition flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#10B981] mb-1 flex items-center gap-2"><FileText className="w-4 h-4" /> My Reviews</div>
              <div className="text-[#1A1A1A] text-lg">See every review you've submitted, with response &amp; reciprocal status.</div>
            </div>
            <ArrowRight className="w-5 h-5 text-[#10B981]" />
          </Link>
          <Link to="/notifications" data-testid="link-inbox"
            className="rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-[#FF1A82]/40 glow-lime p-7 hover:-translate-y-1 transition flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] mb-1 flex items-center gap-2"><Bell className="w-4 h-4" /> Inbox</div>
              <div className="text-[#1A1A1A] text-lg">Notifications for profiles you've claimed.</div>
            </div>
            <ArrowRight className="w-5 h-5 text-[#FF1A82]" />
          </Link>
          {user.claimed_profile_id && (
            <Link to={`/profile/${user.claimed_profile_id}`} data-testid="link-public-profile"
              className="rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-[#10B981]/40 glow-pink p-7 hover:-translate-y-1 transition flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#10B981] mb-1 flex items-center gap-2"><User className="w-4 h-4" /> My Public Profile</div>
                <div className="text-[#1A1A1A] text-lg">See your profile exactly as the community sees it.</div>
              </div>
              <ArrowRight className="w-5 h-5 text-[#10B981]" />
            </Link>
          )}
          {user.is_admin && (
            <Link to="/admin/stats" data-testid="link-admin-stats"
              className="rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-[#FF1A82]/40 glow-lime p-7 hover:-translate-y-1 transition flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] mb-1 flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Admin Stats</div>
                <div className="text-[#1A1A1A] text-lg">Site traffic, recent visits and tarot purchases.</div>
              </div>
              <ArrowRight className="w-5 h-5 text-[#FF1A82]" />
            </Link>
          )}
        </div>

        <ChangePhoneCard user={user} refresh={refresh} />
        {user.is_admin && <AdminHealthPanel />}
      </section>
    </PageBg>
  );
}

function AdminHealthPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.adminHealth().then(setData).catch(() => setErr(true));
  }, []);
  const good = (v) => (v === "ok" || v === "configured" || v === "LIVE" || /texts left/.test(String(v))) && !/LOW/.test(String(v));
  return (
    <div className="mt-6 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border-2 border-[#10B981]/40 p-7" data-testid="admin-health-panel">
      <div className="text-xs uppercase tracking-[0.22em] text-[#10B981] mb-1 flex items-center gap-2"><Activity className="w-4 h-4" /> Backend Health · Admin only</div>
      <p className="text-lg text-[#1A1A1A] font-semibold">Live status of every system, so you can investigate reports right away.</p>
      {err && <p className="mt-4 text-lg text-[#FF1A82] font-bold" data-testid="admin-health-error">Couldn't load health data — the backend may be down.</p>}
      {!data && !err && <p className="mt-4 text-lg text-[#5A5A5A] font-semibold">Checking systems…</p>}
      {data && (
        <>
          <div className="mt-5 flex flex-wrap gap-3" data-testid="admin-health-checks">
            {Object.entries(data.checks).map(([k, v]) => (
              <span key={k} data-testid={`health-${k.replace(/_/g, "-")}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-lg font-bold border-2 ${good(v) ? "border-[#10B981]/50 bg-[#10B981]/10 text-[#10B981]" : "border-[#FF1A82]/50 bg-[#FF1A82]/10 text-[#FF1A82]"}`}>
                {good(v) ? "●" : "▲"} {k.replace(/_/g, " ")}: {v}
              </span>
            ))}
          </div>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="admin-health-counts">
            {Object.entries(data.counts).map(([k, v]) => (
              <div key={k} className="rounded-2xl border border-black/10 bg-white/70 backdrop-blur-lg p-4">
                <div className="text-lg text-[#5A5A5A] font-semibold capitalize">{k.replace(/_/g, " ")}</div>
                <div className="font-heading text-3xl text-[#1A1A1A]">{v}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-lg text-[#5A5A5A] font-semibold">Server time: {new Date(data.server_time).toLocaleString()}</p>
          {(data.recent_errors || []).length > 0 && (
            <div className="mt-4 rounded-2xl border border-[#FF1A82]/40 bg-[#FF1A82]/5 p-4" data-testid="recent-errors">
              <div className="text-lg font-bold text-[#FF1A82] mb-2">Recent backend errors</div>
              {data.recent_errors.map((e, i) => (
                <p key={i} className="text-lg text-[#5A5A5A] font-mono break-all">{e}</p>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ChangePhoneCard({ user, refresh }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("enter");
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.phoneStart(phone);
      setStage("verify");
      toast.success("Code texted to your new number!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the code.");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.phoneVerify(code);
      if (res.token) localStorage.setItem("welp_token", res.token);
      toast.success("Phone number updated!");
      setOpen(false); setStage("enter"); setPhone(""); setCode("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wrong code — try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7" data-testid="change-phone-card">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#10B981] mb-1 flex items-center gap-2"><Phone className="w-4 h-4" /> Phone number</div>
          <div className="text-[#1A1A1A] text-lg font-semibold">{user.phone_hint ? `Verified number ending in ${user.phone_hint}` : "No verified number on file."}</div>
        </div>
        <button onClick={() => setOpen(o => !o)} data-testid="change-phone-toggle"
          className="px-5 py-2.5 rounded-full bg-[#10B981] text-white font-bold text-lg hover:brightness-110">
          {open ? "Cancel" : "Change number"}
        </button>
      </div>
      {open && stage === "enter" && (
        <form onSubmit={sendCode} className="mt-5 flex flex-wrap gap-3 items-center">
          <input data-testid="change-phone-input" required value={phone} onChange={e => setPhone(e.target.value)}
            placeholder="New phone number" type="tel"
            className="flex-1 min-w-[220px] bg-white/60 border border-black/15 rounded-xl px-4 py-3 text-lg text-[#1A1A1A] font-semibold outline-none focus:border-[#10B981]" />
          <button disabled={busy} data-testid="change-phone-send"
            className="px-6 py-3 rounded-full bg-[#FF1A82] text-white font-bold text-lg hover:brightness-110 disabled:opacity-60">
            {busy ? "Sending…" : "Text me a code"}
          </button>
        </form>
      )}
      {open && stage === "verify" && (
        <form onSubmit={verify} className="mt-5 flex flex-wrap gap-3 items-center">
          <input data-testid="change-phone-code" required value={code} onChange={e => setCode(e.target.value)}
            placeholder="6-digit code" inputMode="numeric" maxLength={8}
            className="flex-1 min-w-[180px] bg-white/60 border border-black/15 rounded-xl px-4 py-3 text-lg text-[#1A1A1A] font-semibold outline-none focus:border-[#FF1A82]" />
          <button disabled={busy} data-testid="change-phone-verify"
            className="px-6 py-3 rounded-full bg-[#FF1A82] text-white font-bold text-lg hover:brightness-110 disabled:opacity-60">
            {busy ? "Verifying…" : "Verify"}
          </button>
        </form>
      )}
    </div>
  );
}

export function MyReviewsPage() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    api.myReviews().then(d => setItems(d.reviews || [])).finally(() => setLoading(false));
  }, [user, authLoading, nav]);

  return (
    <PageBg>
      <Nav />
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-[0.32em] mb-3">
          <span className="text-[#10B981]">Welp</span> <span className="text-[#5A5A5A]">·</span> <span className="text-[#FF1A82]">Dating With Integrity</span> <span className="text-[#5A5A5A]">·</span> My Reviews
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">Reviews <span className="italic text-[#FF1A82]">you've shared.</span></h1>
        <p className="text-[#3A3A3A] mt-3 max-w-xl">Track every review you've submitted, see if the reviewed person responded, and whether they wrote a reciprocal review of you.</p>

        <div className="mt-10 space-y-4">
          {loading && <p className="text-[#5A5A5A]">Loading…</p>}
          {!loading && items.length === 0 && (
            <div className="rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-8" data-testid="my-reviews-empty">
              <p className="text-[#3A3A3A]">You haven't submitted any reviews yet.</p>
              <Link to="/review/new" className="inline-block mt-4 px-6 py-3 rounded-full bg-[#FF1A82] text-white font-semibold glow-lime hover:brightness-110 transition">Share your first experience</Link>
            </div>
          )}
          {items.map((r, i) => (
            <article key={r.review_id} data-testid={`my-review-${r.review_id}`}
              className="rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-6 hover:border-[#FF1A82]/40 transition animate-fade-up flex flex-col md:flex-row md:items-center gap-5" style={{ animationDelay: `${i*60}ms` }}>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <span className="font-heading text-2xl text-[#1A1A1A] italic">{r.username}</span>
                  <span className="text-xs uppercase tracking-[0.22em] text-[#FF1A82]">{r.platform}</span>
                  <span className="text-xs text-[#5A5A5A]">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-[#3A3A3A] text-sm line-clamp-2">{r.positive_moment}</p>
                <div className="flex flex-wrap gap-2 mt-3 text-xs">
                  <span className="px-3 py-1 rounded-full bg-[#FF1A82]/15 text-[#FF1A82] border border-[#FF1A82]/30 inline-flex items-center gap-1"><FileText className="w-3 h-3" /> Published</span>
                  {r.has_response ? (
                    <span className="px-3 py-1 rounded-full bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 inline-flex items-center gap-1" data-testid={`badge-response-${r.review_id}`}><MessageSquare className="w-3 h-3" /> Response received</span>
                  ) : (
                    <span className="px-3 py-1 rounded-full bg-black/5 text-[#5A5A5A] border border-black/10 inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Awaiting response</span>
                  )}
                  {r.has_reciprocal && (
                    <span className="px-3 py-1 rounded-full bg-yellow-400/15 text-yellow-300 border border-yellow-400/40 inline-flex items-center gap-1" data-testid={`badge-reciprocal-${r.review_id}`}><RotateCw className="w-3 h-3" /> Reciprocal review</span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Link to={`/profile/${r.profile_id}`} className="px-5 py-2.5 rounded-full bg-[#FF1A82] text-white font-semibold text-sm glow-lime hover:brightness-110 transition">View Skinny</Link>
              </div>
            </article>
          ))}
        </div>
      </section>
    </PageBg>
  );
}
